import React, { useEffect, useState, useCallback } from "react";
import { Button, OverlayTrigger, Popover, ListGroup, ListGroupItem } from "react-bootstrap";
import { PageTitle } from "../../PageTitle";
import i18next from "i18next";
import { DatePickerInput } from "../../DatePickerInput";
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import { faChevronRight, faBars } from "@fortawesome/fontawesome-free-solid/";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import ScrollArea from "react-scrollbar";
import { ScrollModule } from "../../ScrollModule";
import config from "config";

import { FormatHelper, ReportDataHelper, debounce } from "../../../helpers";

import { DashboardService, AlertStatusesService, AlertsService } from "../../../services";

import {
  NADLocationTableCell,
  NADEventTableCell,
  NADNameTableCell,
  NADFormattedTime,
} from "./needs_attention_components";

const TableHeaderEntry = ({ title, subTitle }) => {
  return (
    <div>
      <div>
        <div>{title}</div>
        <span>{subTitle}</span>
      </div>
    </div>
  );
};

const TableTextFilter = ({ onChange }) => {
  return (
    <div className="filter dashboard-table-search">
      <input
        style={{ width: "100%", margin: "0 auto" }}
        type="text"
        name="search"
        className="table-search"
        autoComplete="off"
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
};

const TableDateFilter = ({ date, onDayChange }) => {
  return (
    <div className={`filter dashboard-table-datepicker${date != null ? " has-value" : " no-value"}`}>
      <DatePickerInput
        meta={{}}
        input={{ value: date }}
        onDayChange={(updatedDate) => { onDayChange(updatedDate); }}
        clearDate={() => { onDayChange(null); }}
      />
    </div>
  );
};

const convertEventDataToThreshold = (eventData) => {
  return {
    displayableEvaluationStringValue:
      eventData.displayableEvaluationStringValue,
    displayableEvaluationNumericValue:
      eventData.displayableEvaluationNumericValue,
    displayableEvaluationBooleanValue:
      eventData.displayableEvaluationBooleanValue,
    deviceDataType: {
      token: eventData.deviceDataTypeToken,
      deviceDataDisplayType: { format: eventData.deviceDataDisplayTypeFormat },
    },
  };
};

const findLocationById = (availableLocations, locationId) => {
  var location = null;
  availableLocations.forEach((availableLocation) => {
    if (availableLocation.value == locationId) {
      location = availableLocation.grouping;
    }
  });
  return location;
};

const ActionPopover = ({data, locationClicked, updateStatus, filterOptions, ...props}) => {
  const {...rest} = props;
  const statusTitle = data.alertStatusToken;
  const isAutoResolved = data.alertStatusToken === 'auto_resolved';
  return (
      <Popover id="actions-popover-small" {...rest} >
        <div className='custom-popover'>
          <div className='custom-popover-title'>{ i18next.t('pages.alerts.actions') }</div>
          <ListGroup className="list-unstyled">
            <ListGroupItem>
            <Link to={`/monitoring/locations/${data.locationId}/events/${data.eventId}`}>
              {i18next.t("pages.locations-needing-attention.table.event-detail")}{" "}
              <FontAwesomeIcon icon={faChevronRight} size="xs" />
            </Link>
            </ListGroupItem>
              <ListGroupItem>
              <a onClick={() => { locationClicked({grouping: {id: data.locationId}}) }}>
              {i18next.t("pages.locations-needing-attention.table.view-location")}{" "}
              <FontAwesomeIcon icon={faChevronRight} size="xs" />
            </a>
            </ListGroupItem>
          </ListGroup>
          <div className='custom-popover-title'>{ isAutoResolved ? i18next.t('pages.alerts.event-status') : i18next.t('pages.alerts.event-status-change') }</div>
          <ul className="list-unstyled status-controls">
            {
              isAutoResolved ? <span>{filterOptions.filter(option => option.status.token === 'auto_resolved').map(option => {option.name})}</span> :
              filterOptions.filter(option => option.status.token !== 'auto_resolved')
                .filter(option => option.status.token !== 'unacknowledged')
                         .map((option, index) => (
                  <li key={`ActionPopover_AlertStatus_${index.toString()}`}>
                    <div className="radio">
                      <input type="radio" id={`alert_status_${option.name.toLowerCase()}`} checked={statusTitle === option.status.token} onChange={() => updateStatus(option.status, data.eventId)} />
                      <label htmlFor={`alert_status_${option.name.toLowerCase()}`}>{option.name}</label>
                    </div>
                  </li>
              ))
            }
          </ul>
        </div> 
      </Popover>
  )
};

const CriticalLocationsArea = ({availableLocations, isMobile, showEntityDetail, locationClicked}) => {
  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(null);
  const [alertStatusOptions, setAlertStatusOptions] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [eventLevelToken, setEventLevelToken] = useState("critical");
  const [filters, setFilters] = useState({locationName: null, eventName: null, entityName: null, areaName: null, occurredAt: null});
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [sortedValues, setSortedValues] = useState([{desc: true, id: "occurredAt"}]);

  const performQuery = useCallback((token, passedEventLevelToken, passedPageSize, page, filters, sorts) => {
      setIsLoading(true);
      setPageCount(0);
      setTotalRows(0);
      setEventsData([]);

      let requests = [];

      requests.push(
        DashboardService.getReportOrDashboardDataByToken(token, passedPageSize, page, { ...filters, eventLevelToken: passedEventLevelToken }, ReportDataHelper.convertSortRequestValues(sorts))
          .then((response) => {
            if (response.reportResult) {
                setPageCount(Math.ceil(response.data.totalCount / passedPageSize));
                setTotalRows(response.data.totalCount);
                setEventsData(ReportDataHelper.convertQueryResponse(response.reportResult));
            } else {
                setCurrentPage(0);
                setPageCount(0);
                setTotalRows(0);
                setEventsData([]);
            }
          })
    );

    requests.push(
      AlertStatusesService.listAll()
        .then((response) => {
          let alertStatusOptions = response.alertStatuses.map((status) => ({
              id: status.id,
              icon: null,
              className: "",
              name: i18next.t(`alert-statuses.${status.token}`),
              status: status,
          }));
          setAlertStatusOptions(alertStatusOptions);
      })
    );
        
    Promise.all(requests)
        .then((_) => {
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });
  }, []);

  const performDelayedQuery = useCallback(
    debounce(() => {
      // This will trigger the effect to reload the table and rerun the query
      setCurrentPage(0);
      setFiltersChanged(true);
    }, 500),
    []
  );

  const updateStatus = (newStatus, rowId) => {
    AlertsService.updateStatus(rowId, newStatus).then(_ => {
      performQuery("locations_needing_attention_table", eventLevelToken, pageSize, currentPage, filters, sortedValues);
    }).catch(_ => {
      console.error(error);
    })
  }

  const renderOverlay = (cell) => {
    const overlay =
        <ActionPopover
          data={cell.original}
          locationClicked={locationClicked}
          updateStatus={updateStatus}
          filterOptions={alertStatusOptions}
        />;
      
    return (
        <OverlayTrigger trigger="click" rootClose placement="left" overlay={overlay} >
          <Button className="btn btn-sm btn-default">
            <FontAwesomeIcon icon={faBars} />
          </Button>
        </OverlayTrigger>
    );
  };

  useEffect(() => {
    if (pageSize != null) {
      performQuery("locations_needing_attention_table", eventLevelToken, pageSize, currentPage, filters, sortedValues);
      setFiltersChanged(false);
    }
  }, [availableLocations, pageSize, currentPage, sortedValues, filtersChanged]);

  useEffect(() => {
    if (pageSize != null) {
      performDelayedQuery();
    }
  }, [availableLocations, filters]);

  useEffect(() => {
    setCurrentPage(0);
    setFiltersChanged(true);
  }, [eventLevelToken]);

  const primaryColumns = [
    {
      Header: <TableHeaderEntry title={i18next.t("pages.locations-needing-attention.table.unresolved-events")} subTitle={i18next.t("pages.locations-needing-attention.table.classification")} />,
      accessor: "eventName",
      sortable: true,
      filterable: true,
      Filter: <TableTextFilter onChange={(filterValue) => setFilters({ ...filters, eventName: filterValue })} />,
      Cell: (cell) => 
        <NADEventTableCell
          isMobile={isMobile}
          alertId={cell.original.eventId}
          occurredAt={<NADFormattedTime locationTimezone={cell.original.locationTimezone} timestamp={cell.original.occurredAt} newline={false} />}
          displayName={cell.original.entityName}
          entitySerialNumber={cell.original.entitySerialNumber}
          deviceDataTypeToken={cell.original.deviceDataTypeToken}
          thresholdClassificationToken={cell.original.thresholdClassificationToken}
          thresholdClassificationName={cell.original.thresholdClassificationName}
          deviceDataTypeName={cell.original.deviceDataTypeName}
          thresholdEvaluationTypeName={cell.original.thresholdEvaluationTypeShortForm}
          measureName={FormatHelper.formatMeasurement(convertEventDataToThreshold(cell.original))}
          alertConfigurationName={cell.original.eventName}
          locationId={cell.original.locationId}
        />
    },
    {
      Header: <TableHeaderEntry title={i18next.t("pages.locations-needing-attention.table.location")} subTitle={i18next.t("pages.locations-needing-attention.table.district")} />,
      accessor: "locationName",
      sortable: true,
      filterable: true,
      Filter: <TableTextFilter onChange={(filterValue) => setFilters({ ...filters, locationName: filterValue, })} />,
      Cell: (cell) => <NADLocationTableCell location={{ id: cell.original.locationId, name: cell.original.locationName, grouping: findLocationById(availableLocations, cell.original.locationId) }} locationClicked={locationClicked} />
    },
    {
      Header: <TableHeaderEntry title={i18next.t("pages.locations-needing-attention.table.asset")} />,
      accessor: "entityName",
      sortable: true,
      filterable: true,
      Filter: <TableTextFilter onChange={(filterValue) => setFilters({...filters, entityName: filterValue}) } />,
      Cell: (cell) => <NADNameTableCell entity={{serialNumber: cell.original.entitySerialNumber, displayName: cell.original.entityName}} onClick={() => {showEntityDetail(cell.original.entitySerialNumber, cell.original.areaName) }} />,
      width: 200,
    },
    {
      Header: <TableHeaderEntry title={i18next.t("pages.locations-needing-attention.table.area")} />,
      accessor: "areaName",
      sortable: true,
      filterable: true,
      Filter: <TableTextFilter onChange={(filterValue) => setFilters({ ...filters, areaName: filterValue, }) } />,
    },
    {
      Header: <TableHeaderEntry title={i18next.t("pages.locations-needing-attention.table.event-start")} />,
      accessor: "occurredAt",
      sortable: true,
      filterable: true,
      Filter: <TableDateFilter date={ filters.occurredAt && new Date(filters.occurredAt) } onDayChange={(filterValue) => setFilters({ ...filters, occurredAt: filterValue && filterValue.valueOf() }) } />,
      Cell: ({original}) => <NADFormattedTime locationTimezone={original.locationTimezone} timestamp={original.occurredAt} newline={true} />,
    },
    {
      Header: <TableHeaderEntry />,
      accessor: "",
      sortable: false,
      Cell: cell => renderOverlay(cell),
      sortable: false,
      width: 60,
      minResizeWidth: 50
    }
  ];

  return (
    <div className="row">
      <div className="col-xs-12">
        <div className="page-title-with-icon">
          <img src={`${config.WEB_BASE_URL}/images/icon/locations-needing-atention-icon.svg`} className="icon icon--medium" />
          <PageTitle title={i18next.t("pages.locations-needing-attention.attention-header")} />
        </div>

        <div className="op-content-box">
          <div className="event-type-filter">
            <div className={eventLevelToken === "critical" ? "selected" : undefined} onClick={() => setEventLevelToken("critical")} >
              <img src={`${config.WEB_BASE_URL}/images/icon/critical-event-filter-icon.svg`} className="icon icon--small" />
              {i18next.t("pages.locations-needing-attention.event-type-critical")}
            </div>
            <div className={eventLevelToken === "warning" ? "selected" : undefined} onClick={() => setEventLevelToken("warning")}>
              <img src={`${config.WEB_BASE_URL}/images/icon/warning-event-filter-icon.svg`} className="icon icon--small" />
              {i18next.t("pages.locations-needing-attention.event-type-warning")}
            </div>
          </div>
          <ScrollModule />
          <ScrollArea speed={0.8} className="scroll-container" contentClassName="scrollable-content" horizontal>
            <div className="react-table-custom">
              <ReactTable
                className="-striped -highlight monitoring-dashboard-table"
                manual // Forces table not to paginate or sort automatically, so we can handle it server-side
                data={eventsData}
                pages={pageCount} // Display the total number of pages
                loading={isLoading} // Display the loading overlay when we need it
                showPaginationTop
                showResolveButton={false}
                totalElements={totalRows}
                page={currentPage}
                pageSize={pageSize || 10}
                onFetchData={({ page, pageSize, sorted }) => {
                  setCurrentPage(page);
                  setPageSize(pageSize);
                  setSortedValues(sorted.length > 0 ? sorted : sortedValues);
                }}
                columns={primaryColumns}
              />
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default CriticalLocationsArea;
